const readTextField = (field) => {
	if (field === null || field === undefined || field === "") return "";
	if (typeof field === "object") {
		return String(field.name ?? field.label ?? field.value ?? "").trim();
	}
	return String(field).trim();
};

const capitalize = (value) => {
	if (!value) return "";
	const text = String(value).trim();
	return text.charAt(0).toUpperCase() + text.slice(1);
};

const pluralizeUnitLabel = (label) => {
	if (!label) return "";
	const word = String(label).trim();
	const lower = word.toLowerCase();
	if (lower.endsWith("s")) return word;
	if (lower === "box") return "Boxes";
	return `${word}s`;
};

const PACKAGE_STOCK_UNITS = new Set([
	"strip",
	"bottle",
	"tube",
	"can",
	"box",
	"vial",
	"sachet",
	"packet",
	"ampoule",
	"kit",
]);

/** Short stock unit for the compact badge (e.g. Tablets, Tubes, Strips). */
export const getStockUnitLabel = (item) => {
	const pkg = getPackageLabel(item);
	if (pkg) {
		const lower = pkg.toLowerCase();
		if (PACKAGE_STOCK_UNITS.has(lower)) {
			return pluralizeUnitLabel(pkg);
		}
	}

	const form = getProductForm(item);
	if (form) {
		const lower = form.toLowerCase();
		if (lower.includes("tablet")) return "Tablets";
		if (lower.includes("capsule")) return "Capsules";
		if (lower.includes("syrup") || lower.includes("suspension")) return "Bottles";
		if (lower.includes("cream") || lower.includes("ointment") || lower.includes("gel")) {
			return pkg ? pluralizeUnitLabel(pkg) : "Units";
		}
		if (lower.includes("spray") || lower.includes("drop")) return "Units";

		const firstWord = form.split(/[\s/-]+/)[0];
		if (firstWord.length <= 9) return pluralizeUnitLabel(firstWord);
		return `${firstWord.slice(0, 8)}…`;
	}

	if (pkg) return pluralizeUnitLabel(pkg);
	return "";
};

export const getProductForm = (item) =>
	readTextField(item?.productForm ?? item?.product_form);

export const getPackageLabel = (item) => readTextField(item?.package);

export const getPackageQuantity = (item) => {
	const qty = item?.package_qty ?? item?.quantity ?? item?.packageQuantity;
	if (qty === null || qty === undefined || qty === "") return null;
	const num = Number(qty);
	return Number.isFinite(num) ? num : qty;
};

export const getPackagingDetail = (item) => {
	const data = item ?? {};
	return String(
		data.packaging_detail ??
			data.package_details ??
			data.packageDetails ??
			""
	).trim();
};

/** Optional meta `value` from search / saved item payloads. */
export const getItemMetaValue = (item) => {
	const data = item ?? {};
	const raw = data.value ?? data.package_value ?? data.packageValue;
	return readTextField(raw);
};

export const getPackageSizeLabel = (item) =>
	getPackagingDetail(item) || getItemMetaValue(item);

/** Short size labels (e.g. "30 g") fit on the card chip; long text stays in popover. */
export const isCompactSizeLabel = (label) => {
	if (!label) return false;
	const text = String(label).trim();
	if (text.length <= 18) return true;
	return /\d+\s*(g|mg|ml|l|kg|mcg|iu)\b/i.test(text);
};

export const formatPackageQtyLine = (packageLabel, quantity) => {
	if (!packageLabel && quantity == null) return "";
	const pkg = capitalize(packageLabel || "Unit");
	if (quantity != null) return `${pkg} ×${quantity}`;
	return pkg;
};

export const getCardSizeLabel = (sizeLabel, quantity) => {
	if (!sizeLabel) return "";
	const text = String(sizeLabel).trim();
	if (quantity != null && text === String(quantity)) return "";
	return text;
};

/** Packaging detail too long for the card chip second line — show in popover. */
export const isPopoverPackagingDetail = (label) => {
	if (!label) return false;
	return String(label).trim().length > 42;
};

export const getProductMeta = (item) => {
	const packageLabel = getPackageLabel(item);
	const quantity = getPackageQuantity(item);

	return {
		productForm: getProductForm(item),
		packageLabel,
		quantity,
		packagingDetail: getPackagingDetail(item),
		sizeLabel: getPackageSizeLabel(item),
		packageQtyLine: formatPackageQtyLine(packageLabel, quantity),
		stockUnitLabel: getStockUnitLabel(item),
	};
};
